import { Fragment, useMemo } from 'react';
import { Divider, Grid, Stack } from '@mui/material';

import { SummaryItemProps, SummaryRowItem } from '../SummaryRowItem';
import Icon from '../../../Icon';
import { GridItem } from './styles';
import type { SummaryCardContentMobileProps } from './types';

export function SummaryCardContentMobile({
  pillColor = false,
  startIconColor,
  summaryValues,
  startIconName,
  defaultValue,
  perLine,
  summary,
}: Readonly<SummaryCardContentMobileProps>) {
  const summaryRows = useMemo(() => {
    if (!Array.isArray(summary)) return [];

    return summary.reduce<SummaryItemProps[][]>((rows, item, index) => {
      const rowIndex = Math.floor(index / perLine);
      rows[rowIndex] = [...(rows[rowIndex] || []), item];
      return rows;
    }, []);
  }, [summary, perLine]);

  return (
    <Stack gap={3}>
      {summaryRows?.map(
        (summaryRow, rowIndex) =>
          Array.isArray(summaryRow) && (
            <Fragment key={`summary-row-${String(rowIndex)}`}>
              <Grid
                alignItems="center"
                flexWrap="wrap"
                flexDirection="row"
                justifyContent="space-between"
                container
              >
                {summaryRow.map((item, summaryIndex) => {
                  const isFirstItem = summaryIndex === 0;
                  const isSecondItem = summaryIndex === 1;
                  const isLastItem = summaryIndex === summaryRow.length - 1;

                  return (
                    <Fragment key={`summary-item-${String(summaryIndex)}`}>
                      {!isFirstItem && (isSecondItem || isLastItem) && (
                        <Divider
                          style={{ marginRight: -1 }}
                          orientation="vertical"
                          variant="middle"
                          flexItem
                        />
                      )}

                      <GridItem
                        item
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="row"
                        display="flex"
                        xs={6}
                        sx={{
                          borderBottom:
                            isFirstItem || isSecondItem
                              ? '1px solid rgba(0, 0, 0, 0.12)'
                              : 'transparent',
                        }}
                      >
                        {isFirstItem && startIconName && (
                          <Icon
                            color={startIconColor}
                            name={startIconName}
                            size={24}
                          />
                        )}
                        <SummaryRowItem
                          value={summaryValues[item.key] ?? defaultValue}
                          pillColor={pillColor}
                          summary={item}
                        />
                      </GridItem>
                      <Divider />
                    </Fragment>
                  );
                })}
              </Grid>

              {summaryRows?.length > 1 &&
                rowIndex !== summaryRows?.length - 1 && (
                  <Divider orientation="horizontal" flexItem />
                )}
            </Fragment>
          ),
      )}
    </Stack>
  );
}
